import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  Radio, RadioGroup, TextField, Typography,
} from '@mui/material';

import {
  lizenzErstellen, lizenzGeraetWechseln, lizenzKeyErzeugen, lizenzKontingent, lizenzModule,
  lizenzSperren, lizenzVerlaengern,
} from '../api';
import { datum, fehlertext, verlaengertUm } from '../utils/format';

const UEBERNAHME = 'Der Kunde muss nichts eintippen: der Schlüssel wird sofort neu signiert und beim nächsten Check-in (stündlich) von ProfiWare übernommen.';

const TITEL = {
  neu: 'Neue Lizenz',
  verlaengern: 'Gültigkeit ändern',
  module: 'Module ändern',
  kontingent: 'Kontingent ändern',
  geraet: 'Gerät wechseln',
  key: 'Schlüssel für Gerät erzeugen',
  key_frei: 'Schlüssel ohne Gerätebindung erzeugen',
  sperren: 'Lizenz sperren',
};
const KNOPF = {
  neu: 'Lizenz anlegen', verlaengern: 'Gültigkeit speichern', module: 'Module speichern', kontingent: 'Kontingent speichern',
  geraet: 'Gerät wechseln', key: 'Schlüssel erzeugen', key_frei: 'Trotzdem erzeugen', sperren: 'Sperren',
};

function Modulwahl({ alleModule, bezeichnungen, gewaehlt, onChange }) {
  const umschalten = (m) => onChange(gewaehlt.includes(m) ? gewaehlt.filter((x) => x !== m) : [...gewaehlt, m]);
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Module</Typography>
        <Button size="small" onClick={() => onChange(gewaehlt.length === alleModule.length ? [] : [...alleModule])}>
          {gewaehlt.length === alleModule.length ? 'Keine' : 'Alle'}
        </Button>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        {alleModule.map((m) => (
          <FormControlLabel
            key={m} label={bezeichnungen[m] || m}
            control={<Checkbox size="small" checked={gewaehlt.includes(m)} onChange={() => umschalten(m)} />}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Ein Dialog fuer alle Aenderungen an einer Lizenz.
 * art: neu | verlaengern | module | kontingent | geraet | key | key_frei | sperren
 */
export default function LizenzDialog({ art, lizenz, kundeId, module, vorgabe, onFertig, onZu }) {
  const [f, setF] = useState({});
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);
  const setze = (teil) => setF((alt) => ({ ...alt, ...teil }));

  useEffect(() => {
    if (!art) return;
    setFehler('');
    setF({
      gueltig_bis: art === 'neu' ? verlaengertUm(null, 12) : (lizenz?.gueltig_bis || ''),
      module: art === 'neu' ? [] : [...(lizenz?.module || [])],
      max_arbeitsplaetze: lizenz?.max_arbeitsplaetze ?? 0,
      max_mandanten: lizenz?.max_mandanten ?? 1,
      hardware_id: vorgabe?.hardware_id || '',
      grund: '',
      wann: 'sofort',
      ab: '',
    });
  }, [art, lizenz, vorgabe]);

  if (!art) return null;

  const speichern = async () => {
    setLaedt(true);
    setFehler('');
    try {
      const plaetze = Math.max(0, parseInt(f.max_arbeitsplaetze, 10) || 0);
      const mandanten = Math.max(1, parseInt(f.max_mandanten, 10) || 1);
      let meldung = '';
      if (art === 'neu') {
        await lizenzErstellen({ kunde_id: kundeId, module: f.module, gueltig_bis: f.gueltig_bis || null, max_arbeitsplaetze: plaetze, max_mandanten: mandanten });
        meldung = 'Lizenz angelegt. Den Aktivierungscode zusammen mit dem Lizenznamen an den Kunden geben.';
      } else if (art === 'verlaengern') {
        await lizenzVerlaengern(lizenz.id, f.gueltig_bis || null);
        meldung = `Gültig bis ${f.gueltig_bis ? datum(f.gueltig_bis) : 'unbefristet'}.`;
      } else if (art === 'module') {
        await lizenzModule(lizenz.id, f.module);
        meldung = 'Module geändert.';
      } else if (art === 'kontingent') {
        await lizenzKontingent(lizenz.id, plaetze, mandanten);
        meldung = 'Kontingent geändert.';
      } else if (art === 'geraet') {
        await lizenzGeraetWechseln(lizenz.id, f.hardware_id.trim());
        meldung = f.hardware_id.trim()
          ? 'Gerät gewechselt. Den neuen Schlüssel an den Kunden geben – der alte ist ungültig.'
          : 'Gerätebindung aufgehoben. Der Kunde kann sich mit seinem Code neu aktivieren.';
      } else if (art === 'key' || art === 'key_frei') {
        await lizenzKeyErzeugen(lizenz.id, art === 'key' ? f.hardware_id.trim() : null, art === 'key_frei');
        meldung = 'Schlüssel erzeugt. Über „Schlüssel kopieren“ an den Kunden geben.';
      } else if (art === 'sperren') {
        await lizenzSperren(lizenz.id, f.grund, f.wann === 'datum' ? f.ab : null);
        meldung = f.wann === 'datum' && f.ab ? `Lizenz wird am ${datum(f.ab)} gesperrt.` : 'Lizenz gesperrt.';
      }
      onFertig(meldung);
    } catch (e) {
      setFehler(fehlertext(e));
    } finally {
      setLaedt(false);
    }
  };

  const gesperrt = laedt
    || (art === 'key' && !f.hardware_id?.trim())
    || (art === 'sperren' && (!f.grund?.trim() || (f.wann === 'datum' && !f.ab)));
  const aktiviert = !!lizenz?.lizenz_key;
  const farbe = art === 'sperren' ? 'error' : (art === 'key_frei' ? 'warning' : 'primary');

  const kontingentFelder = (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
      <TextField
        id="lizenz-arbeitsplaetze" label="Arbeitsplätze" type="number" inputProps={{ min: 0 }}
        value={f.max_arbeitsplaetze ?? 0} onChange={(e) => setze({ max_arbeitsplaetze: e.target.value })}
        helperText="Netzwerk-Clients zusätzlich zum Server"
      />
      <TextField
        id="lizenz-mandanten" label="Mandanten" type="number" inputProps={{ min: 1 }}
        value={f.max_mandanten ?? 1} onChange={(e) => setze({ max_mandanten: e.target.value })}
        helperText="Getrennte Betriebe (eigene Datenbanken)"
      />
    </Box>
  );
  const datumFeld = (
    <Box>
      <TextField
        id="lizenz-gueltig-bis" label="Gültig bis" type="date" fullWidth InputLabelProps={{ shrink: true }}
        value={f.gueltig_bis ?? ''} onChange={(e) => setze({ gueltig_bis: e.target.value })}
        helperText={f.gueltig_bis ? '' : 'Leer = unbefristet'}
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
        {[[1, '+ 1 Monat'], [12, '+ 1 Jahr'], [24, '+ 2 Jahre']].map(([monate, text]) => (
          <Button key={monate} size="small" variant="outlined" onClick={() => setze({ gueltig_bis: verlaengertUm(lizenz?.gueltig_bis, monate) })}>
            {text}
          </Button>
        ))}
        <Button size="small" variant="outlined" onClick={() => setze({ gueltig_bis: '' })}>Unbefristet</Button>
      </Box>
    </Box>
  );

  return (
    <Dialog open onClose={laedt ? undefined : onZu} maxWidth="sm" fullWidth>
      <DialogTitle>{TITEL[art]}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '8px !important' }}>
        {fehler && <Alert severity="error">{fehler}</Alert>}

        {art === 'neu' && (
          <>
            <Modulwahl alleModule={module.alle} bezeichnungen={module.bezeichnungen} gewaehlt={f.module || []} onChange={(m) => setze({ module: m })} />
            {kontingentFelder}
            {datumFeld}
          </>
        )}

        {art === 'verlaengern' && (
          <>
            <Typography variant="body2" color="text.secondary">
              Bisher gültig bis {lizenz.gueltig_bis ? datum(lizenz.gueltig_bis) : 'unbefristet'}. Die Knöpfe rechnen ab dem bisherigen Ablauf, bei abgelaufenen Lizenzen ab heute.
            </Typography>
            {datumFeld}
            {aktiviert && <Alert severity="info">{UEBERNAHME}</Alert>}
          </>
        )}

        {art === 'module' && (
          <>
            <Modulwahl alleModule={module.alle} bezeichnungen={module.bezeichnungen} gewaehlt={f.module || []} onChange={(m) => setze({ module: m })} />
            {aktiviert && <Alert severity="info">{UEBERNAHME}</Alert>}
          </>
        )}

        {art === 'kontingent' && (
          <>
            {kontingentFelder}
            <Typography variant="body2" color="text.secondary">
              Verkleinern nimmt dem Kunden nichts weg, was schon angelegt ist – es verhindert nur weitere Arbeitsplätze und Mandanten.
            </Typography>
            {aktiviert && <Alert severity="info">{UEBERNAHME}</Alert>}
          </>
        )}

        {art === 'geraet' && (
          <>
            <Alert severity="warning">
              Alle bisher ausgestellten Schlüssel dieser Lizenz werden ungültig. ProfiWare auf dem alten Gerät sperrt sich beim nächsten Check-in.
            </Alert>
            <TextField
              id="lizenz-hardware-id" label="Hardware-ID des neuen Geräts" value={f.hardware_id ?? ''} autoFocus
              onChange={(e) => setze({ hardware_id: e.target.value })} placeholder="AB12-CD34-EF56-7890-1234-5678"
              helperText="Mit Hardware-ID entsteht sofort der neue Schlüssel. Leer lassen: der Kunde aktiviert sich mit seinem Code selbst neu."
            />
          </>
        )}

        {art === 'key' && (
          <>
            <Typography variant="body2" color="text.secondary">
              Erzeugt den Schlüssel sofort – der Kunde muss die Aktivierungsseite nicht aufrufen, sondern fügt ihn nur ein. Die Hardware-ID steht im Installer und in ProfiWare unter Einstellungen → Lizenz.
            </Typography>
            <TextField
              id="lizenz-hardware-id" label="Hardware-ID des Kundengeräts" value={f.hardware_id ?? ''} autoFocus
              onChange={(e) => setze({ hardware_id: e.target.value })} placeholder="AB12-CD34-EF56-7890-1234-5678"
              helperText="6 Blöcke à 4 Zeichen"
            />
          </>
        )}

        {art === 'key_frei' && (
          <Alert severity="warning">
            Dieser Schlüssel enthält <strong>keine</strong> Hardware-ID. Er läuft auf beliebig vielen Rechnern, begrenzt nur durch das Ablaufdatum
            ({lizenz.gueltig_bis ? datum(lizenz.gueltig_bis) : 'unbefristet'}). Gedacht für Demo- und Testinstallationen, nicht für Kunden.
          </Alert>
        )}

        {art === 'sperren' && (
          <>
            <TextField
              id="lizenz-sperrgrund" label="Grund" value={f.grund ?? ''} autoFocus multiline minRows={2}
              onChange={(e) => setze({ grund: e.target.value })}
              helperText="Nur für das Protokoll. Der Kunde sieht den Grund nicht, nur „Lizenz gesperrt – bitte an ProfiWare wenden“."
            />
            <RadioGroup value={f.wann ?? 'sofort'} onChange={(e) => setze({ wann: e.target.value })}>
              <FormControlLabel value="sofort" control={<Radio size="small" />} label="Sofort – ProfiWare sperrt sich beim nächsten Check-in (spätestens nach einer Stunde)" />
              <FormControlLabel value="datum" control={<Radio size="small" />} label="Ab einem Datum – bis dahin zeigt ProfiWare einen Hinweis" />
            </RadioGroup>
            {f.wann === 'datum' && (
              <TextField
                id="lizenz-sperre-ab" label="Sperren ab" type="date" InputLabelProps={{ shrink: true }}
                value={f.ab ?? ''} onChange={(e) => setze({ ab: e.target.value })}
              />
            )}
            <Typography variant="body2" color="text.secondary">Eine Sperre lässt sich jederzeit wieder aufheben.</Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onZu} disabled={laedt}>Abbrechen</Button>
        <Button variant="contained" color={farbe} onClick={speichern} disabled={gesperrt}>{laedt ? 'Bitte warten…' : KNOPF[art]}</Button>
      </DialogActions>
    </Dialog>
  );
}
